import * as React from 'react';
import { queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import {
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
  useMasonryEngineActionEmitter,
  MasonryEngineConfigContext,
} from '@leagueplatform/masonry-engine';
import { MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import { NODE_TYPE_CHAT_HISTORY_CONTAINER } from '../../../constants/masonry.constants';
import { ChatHistoryContainer } from '../../../components/chat-history-container/chat-history-container.component';
import { clearChatSelection } from '../../chat-selection-store';

type ChatHistoryContainerNodeProperties = {
  title: string;
  subtitle: string;
  isLoading?: boolean;
};

type ChatHistoryContainerNodeActions = {
  onBackClick?: MasonryDriverAction;
};

type ChatHistoryContainerNodeSections = {
  chatMessages?: MasonryEngineNode<string, any>[];
};

type ChatHistoryContainerNode = MasonryEngineNode<
  typeof NODE_TYPE_CHAT_HISTORY_CONTAINER,
  ChatHistoryContainerNodeProperties,
  ChatHistoryContainerNodeActions,
  ChatHistoryContainerNodeSections
>;

export type ChatHistoryContainerRendererProps =
  MasonryEngineNodeRendererProps<ChatHistoryContainerNode>;

export const ChatHistoryContainerRenderer: (
  props: ChatHistoryContainerRendererProps,
) => React.ReactElement = ({
  title,
  subtitle,
  onBackClick,
  chatMessages,
  rootElementRef,
  isReloading,
  isLoading,
}: ChatHistoryContainerRendererProps) => {
  const invokeAction = useMasonryEngineActionEmitter();
  const isMobileTablet = useMediaQuery(queryHelpers.down('laptop'));
  const isChatLoading = isLoading || isReloading;
  const masonryConfig = React.useContext(MasonryEngineConfigContext);

  const handleBackClick = () => {
    if (onBackClick) {
      invokeAction(onBackClick);
    }

    // Back-nav only fires on mobile (button is gated on `isMobileTablet`),
    // where the list replaces the chat pane — keeping `aria-current` on
    // the previously selected item would be misleading.
    clearChatSelection();

    const stateMap =
      masonryConfig?.stateControllerStore.getState().nodeStateMap;
    if (!stateMap) return;

    // Read state imperatively at click time to avoid subscribing to the
    // store and causing re-renders on every state update.
    let chatListNodeId: string | undefined;
    Array.from(stateMap.entries()).forEach(([nodeId, nodeState]) => {
      if (nodeState?.hasActiveChat) {
        chatListNodeId = nodeId;
        invokeAction({
          type: 'setNodeState',
          namespace: 'default',
          payload: {
            nodeId,
            state: {
              hasActiveChat: false,
              selectedChat: undefined,
            },
          },
        });
      }
      if (nodeState?.isSelected) {
        invokeAction({
          type: 'setNodeState',
          namespace: 'default',
          payload: {
            nodeId,
            state: { isSelected: false },
          },
        });
      }
    });

    // Deferred to the next frame because the list is not yet visible
    // when hasActiveChat is cleared — React needs to re-render first.
    if (chatListNodeId) {
      requestAnimationFrame(() => {
        masonryConfig?.messagingController.sendMessage(
          chatListNodeId as string,
          {
            type: 'focus',
            payload: { scroll: false },
          },
        );
      });
    }
  };

  return (
    <ChatHistoryContainer
      ref={rootElementRef as React.Ref<HTMLDivElement>}
      title={title}
      subtitle={subtitle}
      showBackButton={!!onBackClick && isMobileTablet}
      isLoading={isChatLoading}
      onBackClick={handleBackClick}
    >
      {chatMessages}
    </ChatHistoryContainer>
  );
};
